import React, { useState, useEffect } from "react";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";
import api from "../../api/axois";
import styles from "./UserManagement.module.css";

const userTypes = ["All Users", "New Users", "Old Users"];

const UserManagement = () => {
  const [userData, setUserData] = useState([]);
  const [search, setSearch] = useState("");
  const [userType, setUserType] = useState("All Users");
  const [sort, setSort] = useState("Most Recent");
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    api.get("/user").then((res) => {
      setCurrentUserRole(res.data.role);
    });
  }, []);

  const fetchUsers = () => {
    api.get("/users").then((res) => {
      setUserData(res.data.users || []);
    });
  };

  const filteredData = userData
    .filter((user) => {
      const matchesSearch =
        user.name.toLowerCase().includes(search.toLowerCase()) ||
        user.email.toLowerCase().includes(search.toLowerCase());
      const matchesType =
        userType === "All Users" ||
        (userType === "New Users" && user.bookings <= 3) ||
        (userType === "Old Users" && user.bookings > 3);
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      if (sort === "Most Recent") return b.bookings - a.bookings;
      if (sort === "Oldest") return a.bookings - b.bookings;
      return 0;
    });

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      fetchUsers();
      setDeleteTarget(null);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete user.");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Promote user to admin
  const handlePromote = async (userId) => {
    try {
      await api.post(`/users/${userId}/promote`, { role: "admin" });
      // Refresh user list
      fetchUsers();
      alert("User promoted to admin successfully.");
    } catch (err) {
      alert("Failed to promote user.");
    }
  };

  return (
    <div className={styles.container}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={styles.content}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <div className={styles.innerContent}>
          <h3 className={styles.title}>
            All Users <span className={styles.newIndicator}>{userData.length} Total users</span>
          </h3>
          <div className={styles.controls}>
            <input
              type="text"
              placeholder="Search Name or Email"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              className={styles.input}
            />
            <select
              value={userType}
              onChange={(e) => {
                setUserType(e.target.value);
              }}
              className={styles.select}
            >
              {userTypes.map((type) => (
                <option key={type}>{type}</option>
              ))}
            </select>
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
              }}
              className={styles.select}
            >
              <option>Most Recent</option>
              <option>Oldest</option>
            </select>
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.tableHeader}>Name</th>
                  <th className={styles.tableHeader}>Email</th>
                  <th className={styles.tableHeader}>Phone</th>
                  <th className={styles.tableHeader}>User ID</th>
                  <th className={styles.tableHeader}>Last Login</th>
                  <th className={styles.tableHeader}>Bookings</th>
                  <th className={styles.tableHeader}>Role</th>
                  {currentUserRole === "super_admin" && <th className={styles.tableHeader}>Actions</th>}
                  {currentUserRole === "super_admin" && <th className={styles.tableHeader}>Delete</th>}
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={currentUserRole === "super_admin" ? "9" : "7"} className={styles.noData}>
                      No users found.
                    </td>
                  </tr>
                ) : (
                  filteredData.map((user) => (
                    <tr key={user.id}>
                      <td className={styles.tableCell}>{user.name}</td>
                      <td className={styles.tableCell}>{user.email}</td>
                      <td className={styles.tableCell}>{user.phone}</td>
                      <td className={styles.tableCell}>{user.id}</td>
                      <td className={styles.tableCell}>{user.lastLogin}</td>
                      <td className={styles.tableCell}>{user.bookings}</td>
                      <td className={styles.tableCell}>{user.role}</td>
                      {currentUserRole === "super_admin" && (
                        <td className={styles.tableCell}>
                          {user.role !== "admin" && user.role !== "super_admin" ? (
                            <button
                              className={styles.promoteButton}
                              onClick={() => handlePromote(user.id)}
                            >
                              Promote to Admin
                            </button>
                          ) : (
                            <span className={styles.promoteButtonDisabled}>Admin</span>
                          )}
                        </td>
                      )}
                      {currentUserRole === "super_admin" && (
                        <td className={styles.tableCell}>
                          {user.role !== "admin" && user.role !== "super_admin" ? (
                            <button
                              className={styles.deleteButton}
                              onClick={() => setDeleteTarget({ id: user.id, name: user.name })}
                            >
                              Delete
                            </button>
                          ) : (
                            <span className={styles.deleteButtonDisabled}>—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {sidebarOpen && (
        <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />
      )}

      {deleteTarget && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Delete Account</h3>
            <p className={styles.modalBody}>
              Are you sure you want to permanently delete the account for{" "}
              <strong>{deleteTarget.name}</strong>? This action cannot be undone.
            </p>
            <div className={styles.modalActions}>
              <button
                className={styles.modalCancel}
                onClick={() => setDeleteTarget(null)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className={styles.modalConfirm}
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
